/**
 * /* eslint react/prop-types: 1, no-mixed-spaces-and-tabs: 1
 *
 * @format
 */

import {
	Box,
	Button,
	Container,
	Flex,
	Grid,
	GridItem,
	Input,
	Link,
	Text,
} from '@chakra-ui/react';
import {Form, Formik} from 'formik';
import {GetServerSideProps} from 'next';
import {useRouter} from 'next/router';
import {useEffect, useRef, useState} from 'react';
import * as Yup from 'yup';
import {FormikField} from '../../../components/FormikField/FormikField';
import Layout from '../../../components/Layout';
import contentData from '../../../data/contentData';
import getFooter from '../../../lib/api/acf/footer';
import getMainMenu from '../../../lib/api/acf/mainMenu';
import mappingFooter from '../../../patterns/Footer/mappingFooter';
import HeroBanner from '../../../patterns/HeroBanners/HeroBanner';
import mappingMainMenu from '../../../patterns/MainMenu/mappingMainMenu';
import Spacer from '../../../patterns/Spacer';
import {colors} from '../../../styles/js/colors';
import {textStyles} from '../../../styles/js/textStyles';

// eslint-disable-next-line react/prop-types
function Login({successMessage, errorMessage, seo, mainMenu, footer}) {
	const router = useRouter();
	const [isSubmitLoading, setSubmitLoading] = useState(false);
	const [isLoadingRedirect, setIsLoadingRedirect] = useState(false);
	const [countdown, setCountdown] = useState(5);
	const formRef = useRef<HTMLFormElement>(null);
	const validationSchema = Yup.object().shape({
		password: Yup.string()
			.required('Password is required.')
			.min(8, 'Passwords must be at least 8 characters in length.')
			.matches(
				/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
				'Passwords must be at least 8 characters and contain one number or symbol. Passwords are case sensitive.',
			),
		password2: Yup.string()
			.oneOf([Yup.ref('password')], 'Confirm Password must match Password')
			.required('Confirm Password is required'),
	});
	const initialValues: any = {
		password2: '',
		password: '',
	};

	useEffect(() => {
		let countdownInterval;

		// eslint-disable-next-line @typescript-eslint/no-unsafe-call, react/prop-types
		if (successMessage.includes('Your password has been')) {
			setIsLoadingRedirect(true);
			countdownInterval = setInterval(() => {
				setCountdown(prevCountdown => prevCountdown - 1);
			}, 1000);
		}

		return () => {
			clearInterval(countdownInterval);
		};
	}, [successMessage]);

	useEffect(() => {
		if (countdown === 0) {
			setIsLoadingRedirect(false);
			setCountdown(3);
			void router.push('/members/login');
		}
	}, [countdown, router]);

	const onSubmit = async (values, action) => {
		setSubmitLoading(true);

		if (formRef?.current) {
			const formObj = formRef.current;

			formObj.submit();
		}

		setSubmitLoading(false);
	};

	return (
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		<Layout seo={seo} mainMenu={mainMenu} footer={footer}>
			<HeroBanner
				heroText={contentData.editPassword}
				backgroundColor={'sage.600'}
			/>
			<Box
				as='section'
				overflow='hidden'
				py={{base: '10', sm: '16'}}
				backgroundColor={'white'}>
				<Container maxW='container.xl' mx='auto'>
					<Box w={{base: 'auto', md: '50%'}}>
						{successMessage ? (
							<>
								<Box
									borderWidth={2}
									borderRadius={8}
									borderColor={colors.tweed[500]}
									borderStyle={'dashed'}
									p={10}>
									{successMessage}
									{
										// eslint-disable-next-line @typescript-eslint/no-unsafe-call, react/prop-types
										successMessage.includes('Your password has been') ? (
											<>
												<Spacer />
												<Flex justifyContent={'center'}>
													<Spacer />
													<Button
														href='/members/login'
														as='a'
														isLoading={isLoadingRedirect}
														loadingText={`Redirecting to Login in ${countdown} seconds...`}>
														Redirect to Login
													</Button>
												</Flex>
											</>
										) : null
									}
								</Box>
								<Spacer />
							</>
						) : null}
						<Spacer />
						{errorMessage ? (
							<>
								<Box
									borderWidth={2}
									borderRadius={8}
									borderColor={colors.tweed[500]}
									borderStyle={'dashed'}
									p={10}>
									{errorMessage}
								</Box>{' '}
								<Spacer />
							</>
						) : null}

						{router.query?.key ? (
							<Formik
								// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
								initialValues={initialValues}
								onSubmit={async (values, action) => onSubmit(values, action)}
								validationSchema={validationSchema}>
								{({errors, touched, isSubmitting}) => (
									<Form
										ref={formRef}
										action='/api/member/password-reset?type=resetPassword'
										method='POST'>
										<Text p={{base: '0'}} sx={textStyles.display5LeftDark}>
											Set new password
										</Text>
										<Spacer />
										<FormikField
											errors={errors}
											touched={touched}
											type={'password'}
											label={'Password'}
											placeholder='New Password'
											nameKey={'password'}
											autoComplete={'new-password'}
										/>
										<Text sx={textStyles.paragraph2LefGrey}>
											Passwords must be at least 8 characters and contain one
											number or symbol. Passwords are case sensitive.
										</Text>
										<FormikField
											errors={errors}
											touched={touched}
											type={'password'}
											label={'Confirm Password'}
											nameKey={'password2'}
											autoComplete={'new-password'}
										/>
										<Input
											name='key'
											type={'hidden'}
											value={router.query.key}
										/>
										<Input
											name='username'
											type={'hidden'}
											value={router.query.username}
										/>
										<Spacer size={'lg'} />
										<Button
											type='submit'
											disabled={isSubmitting || isSubmitLoading}
											isLoading={isSubmitLoading}>
											Update password
										</Button>
									</Form>
								)}
							</Formik>
						) : (
							<form
								action='/api/member/password-reset?type=requestResetPasswordLink'
								method='POST'>
								<Grid
									templateColumns={{
										base: 'repeat(1, 1fr)',
										// md: 'repeat(1, 1fr)',
									}}
									gap={{base: '3', md: '6'}}>
									<GridItem>{contentData.passwordResetInstruction}</GridItem>
									<GridItem>
										<Input name='email' placeholder='Email' />
									</GridItem>
									<Spacer size={'lg'} />

									<GridItem>
										<Button
											px={12}
											type='submit'
											float={{base: 'right'}}
											w={{base: '100%', md: 'auto'}}>
											Get new password{' '}
										</Button>
									</GridItem>

									<GridItem>
										<Link variant='textButton' href='/members/login'>
											Log in
										</Link>
										|
										<Link variant='textButton' href='/join-the-assc'>
											Join the ASSC
										</Link>
									</GridItem>
								</Grid>
							</form>
						)}
					</Box>
				</Container>
			</Box>
		</Layout>
	);
}

export const getServerSideProps: GetServerSideProps = async ({
	query,
	params,
	res,
	resolvedUrl,
}) => {
	const {status} = query;

	// const url = new URL(resolvedUrl);
	const urlFullPath = new URL(
		`${process.env.NEXT_PUBLIC_APP_URL}${resolvedUrl}`,
	);

	const {search}: {search: string} = urlFullPath;

	const successMessage = search.includes('key')
		? 'Your password has been successfully updated! You can now proceed to the login page, or you will be automatically redirected there in 5 seconds.'
		: 'Please check your email and follow the link there to reset your password.';

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	let mainMenu = await getMainMenu();
	mainMenu = mappingMainMenu(mainMenu);

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	let footer = await getFooter();
	footer = mappingFooter(footer);

	return {
		props: {
			seo: {
				title: 'Change your Password | Members Area | ASSC',
				description:
					'Review the payment details, edit your card information and manage subscription information assigned to your ASSC membership here.',
			},
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			mainMenu,
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			footer,
			errorMessage:
				status === 'error'
					? 'There was an issue with the password reset. Please try again later.'
					: '',
			successMessage: status === 'success' ? successMessage : '',
		},
	};
};

export default Login;
